/**
 * @file Enrollment Form
 */

/* global wpcf7 */

import { gsap } from 'gsap';

/**
 * Enrollment Form component
 */
const vEnrollmentWrap = () => {
	const enrollmentWrap = document.querySelector( '.v-enrollment-wrap' );

	if ( ! enrollmentWrap ) {
		return;
	}

	const contactForm = enrollmentWrap.querySelector( '.wpcf7' );
	const sizer       = enrollmentWrap.querySelector( '.v-enrollment-wrap__sizer' );
	const nextBtn     = enrollmentWrap.querySelector( '.v-enrollment-form__btn--next' );
	const stepBtns    = enrollmentWrap.querySelectorAll( '.v-enrollment-wrap__step-button' );
	let animating     = false;

	const userCountDropdown = enrollmentWrap.querySelector( 'select[name="number-users"]' );

	/**
	 * Handle step button clicks.
	 *
	 * @param {MouseEvent} e The click event.
	 */
	const handleStepClick = ( e ) => {
		e.preventDefault();

		const btn = e.currentTarget;

		const step = btn.dataset.step;

		if ( btn.hasAttribute( 'disabled' ) || step === sizer.dataset.currentStep ) {
			return;
		}

		moveToStep( step );
	};

	/**
	 * Move to a specific step.
	 *
	 * @param {number} step The step to move to.
	 * @param element
	 */
	const moveToStep = ( step ) => {
		if ( step === parseInt( sizer.dataset.currentStep, 10 ) || animating ) {
			return;
		}

		const stepBtn = enrollmentWrap.querySelector( `.v-enrollment-wrap__step-button[data-target-step="${ step }"]` );
		const tl      = gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: 0.25,
			},
			onStart: () => {
				animating = true;

				if ( step === 3 ) {
					[ ...stepBtns ].forEach( ( btn ) => {
						btn.removeEventListener( 'click', handleStepClick );
						btn.setAttribute( 'disabled', 'disabled' );
					} );
				}
			},
			onComplete: () => {
				stepBtn.removeAttribute( 'disabled' );
				sizer.setAttribute( 'data-current-step', step );
				animating = false;
				gsap.set( sizer, { clearProps: 'height' } );

				if ( step === 3 ) {
					gsap.set( '.v-enrollment-wrap__results', { clearProps: 'height' } );
				}
			},
		} );

		const currentStep      = step === 3 ? enrollmentWrap.querySelector( '.v-enrollment-wrap__form' ) : enrollmentWrap.querySelector( `[data-step="${ sizer.dataset.currentStep }"]` );
		const nextStep         = document.querySelector( `[data-step="${ step }"]` );
		nextStep.style.display = 'block';
		const newHeight        = nextStep.getBoundingClientRect().height;
		nextStep.removeAttribute( 'style' );

		tl.to( currentStep, {
			opacity:    0,
			onComplete: () => {
				currentStep.style.display = 'none';

				if ( step === 3 && nextStep.classList.contains( 'enrollment-result--hide-navi' ) ) {
					enrollmentWrap.classList.add( 'v-enrollment-wrap--hide-navi' );
				}
			},
		} );

		if ( step === 3 ) {
			tl.fromTo( sizer, { height: sizer.getBoundingClientRect().height }, {
				height:   0,
				duration: 0.5,
			} )
				.to( '.v-enrollment-wrap__results', {
					height:   newHeight,
					duration: 0.5,
				} );
		} else {
			tl.to( sizer, {
				height: newHeight,
			}, step !== 3 ? '<' : '' );
		}

		tl.from( nextStep, {
			opacity: 0,
			onStart: () => nextStep.style.display = 'block',
		} );
	};

	nextBtn.addEventListener( 'click', () => {
		// check that step 1 inputs have values
		const stepOne    = enrollmentWrap.querySelector( '.v-enrollment-form__step[data-step="1"]' );
		const stepInputs = stepOne.querySelectorAll( 'input' );
		let stepValid    = true;

		[ ...stepInputs ].forEach( ( input ) => {
			if ( ! input.value || ( input.type === 'email' && ! input.validity.valid ) ) {
				input.classList.add( 'invalid' );
				input.addEventListener( 'input', () => {
					input.classList.remove( 'invalid' );
				} );

				stepValid = false;
			}
		} );

		if ( stepValid ) {
			const api = wpcf7.api.root + wpcf7.api.namespace;
			const id  = contactForm.querySelector( '[name="_wpcf7"]' ).value;
			fetch( `${ api }/contact-forms/${ id }/feedback`, {
				method: 'POST',
				body:   new FormData( contactForm.querySelector( 'form' ) ),
			} )
				.then( ( response ) => response.json() )
				.then( ( json ) => {
					let invalid = false;
					[ ...stepInputs ].forEach( ( input ) => {
						const res  = json.invalid_fields.find( ( i ) => i.field === input.name );
						const wrap = input.closest( '.wpcf7-form-control-wrap' );
						if ( res ) {
							const tip     = document.createElement( 'span' );
							tip.classList.add( 'wpcf7-not-valid-tip' );
							tip.innerText = res.message;
							wrap.appendChild( tip );
							input.classList.add( 'wpcf7-not-valid' );
							input.setAttribute( 'aria-invalid', 'true' );
							invalid       = true;
						} else {
							const tip = wrap.querySelector( '.wpcf7-not-valid-tip' );
							if ( tip ) {
								tip.remove();
							}
							input.classList.remove( 'wpcf7-not-valid' );
							input.setAttribute( 'aria-invalid', 'false' );
						}
					} );

					if ( ! invalid ) {
						moveToStep( 2 );
					}
				} );
		}
	} );

	contactForm.addEventListener( 'wpcf7mailsent', ( { detail } ) => {
		// display the appropriate result div
		const results    = enrollmentWrap.querySelectorAll( '.enrollment-result' );
		const { inputs } = detail;
		let result       = null;

		const enrollData = {};
		inputs.forEach( ( input ) => {
			if ( 'report-provider' === input.name ) {
				const { craId }          = detail.apiResponse;
				enrollData[ input.name ] = craId;
			} else {
				enrollData[ input.name ] = input.value;
			}
		} );

		sessionStorage.setItem( 'v-enrollment-data', JSON.stringify( enrollData ) );

		// Result logic is fall-through, so the first matching result will be displayed
		[ ...results ].forEach( ( r ) => {
			if ( result ) {
				return;
			}

			const { criteria, test, values } = r.dataset;
			const input                      = inputs.find( ( i ) => i.name === criteria );

			if (
				'in' === test &&
				values.split( ',' )
					.map( ( v ) => v.toLowerCase() )
					.includes( input.value.toLowerCase() )
			) {
				result = r;
			}

			if (
				'not-in' === test &&
				! values.split( ',' )
					.map( ( v ) => v.toLowerCase() )
					.includes( input.value.toLowerCase() )
			) {
				result = r;
			}
		} );

		if ( result ) {
			if ( result.classList.contains( 'v-enrollment-result-card' ) ) {
				const tierSpan      = result.querySelector( '.v-tier-tag' );
				const cardWrap      = result.querySelector( '.v-enrollment-result-card__card-wrap' );
				const cardTagValue  = inputs.find( ( i ) => i.name === cardWrap.dataset.tag ).value;
				const cardActiveBtn = cardWrap.querySelector( `.v-pricing-card-toggle__toggle[data-value="${ cardTagValue }"]` ) || cardWrap.querySelector( '.v-pricing-card-toggle__toggle' );

				// modify the result content
				if ( tierSpan ) {
					tierSpan.textContent = inputs.find( ( i ) => i.name === tierSpan.dataset.tag ).value;
				}
				cardActiveBtn.click();

				// vPricingCardToggle;
			}

			result.setAttribute( 'data-step', parseInt( sizer.dataset.currentStep, 10 ) + 1 );
			moveToStep( 3 );
		}
	} );

	[ ...stepBtns ].forEach( ( btn ) => {
		btn.addEventListener( 'click', handleStepClick );
	} );

	document.addEventListener( 'visibilitychange', () => {
		if ( document.visibilityState !== 'visible' ) {
			sessionStorage.removeItem( 'v-enrollment-data' );
		}
	} );

	/**
	 * Handle toggling visibility of "About how many credit
	 * reports are ordered per month" dropdown
	 */
	userCountDropdown.addEventListener( 'change', ( e ) => {
		const selectedValue           = e.target.value;
		const reportsPerMonthWrapper  = enrollmentWrap.querySelector( '.report-volume-select' );
		const reportsPerMonthDropdown = enrollmentWrap.querySelector( 'select[name="report-volume"]' );
		if ( selectedValue === '21+' ) {
			reportsPerMonthWrapper.style.display = 'block';
		} else {
			reportsPerMonthWrapper.style.display  = 'none';
			reportsPerMonthDropdown.selectedIndex = 0;
		}
	} );
};

export default vEnrollmentWrap;
